import _ from 'lodash';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {ActivityVisualTypesEnum} from '../../../src/models/api/activityModel';
import {getActivities, getConversations, getMessages} from '../../../src/store/data/dataSelectors';
import {ViewerPanelPageSelector} from '../../../src/store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelPageTypesEnum} from '../../../src/store/panels/viewer/viewerPanelState';
import {createSelector, wrapInShallowSelector} from '../../helpers/reselectHelpers';

/*
 * Selectors.
 */

export function makeGetViewerState<P>(getViewerPage: ViewerPanelPageSelector<P>) {
  const getViewerPageConversation = makeGetViewerPageConversation(getViewerPage);
  const getViewerPageMessages = wrapInShallowSelector(makeGetViewerPageMessages(getViewerPage));

  return createSelector(
    [getViewerPage, getViewerPageConversation, getViewerPageMessages],
    (viewerPage, conversation, messages) => {
      if (!viewerPage || !conversation || !messages) {
        return undefined;
      }

      return {
        viewerPage,
        conversation,
        messages,
      };
    },
  );
}

function makeGetViewerPageConversation<P>(getViewerPage: ViewerPanelPageSelector<P>) {
  return createSelector([getViewerPage, getConversations], (viewerPage, conversations) => {
    if (
      viewerPage.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
      viewerPage.status !== AsyncStatusesEnum.LOADED
    ) {
      return undefined;
    }

    return conversations[viewerPage.conversationId];
  });
}

function makeGetViewerPageMessages<P>(getViewerPage: ViewerPanelPageSelector<P>) {
  const getViewerPageActivities = createSelector([getViewerPage, getActivities], (viewerPage, activities) => {
    if (
      viewerPage.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
      viewerPage.status !== AsyncStatusesEnum.LOADED
    ) {
      return undefined;
    }

    const viewerPageActivities = viewerPage.activities;
    return _(viewerPageActivities)
      .keys()
      .map((id) => activities[Number(id)])
      .compact()
      .value();
  });

  return createSelector([getViewerPageActivities, getMessages], (viewerPageActivities, messages) =>
    _(viewerPageActivities)
      .sortBy((a) => a.date)
      .map((a) => {
        if (a.visualType !== ActivityVisualTypesEnum.MESSAGE) {
          return null;
        }

        return messages[a.messageUid];
      })
      .compact()
      .value(),
  );
}
